import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SeoHead from '@/components/feature/SeoHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { siteInfo, brandValues, faqs } from '@/mocks/site';
import { useState } from 'react';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9qqeh7nahb7382ovdkg';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formUrl] = useState<string>(FORM_SUBMIT_URL);
  const [formStatus, setFormStatus] = useState<'' | 'success' | 'error'>('');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const errMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        setFormError(errMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SeoHead
        title="关于我们 - 国产在线观看平台介绍"
        description={`${siteInfo.name}致力于为用户提供海量高清国产视频免费在线观看服务，涵盖电视剧、电影、综艺、短片、动漫等分类，每日更新最热门国产影视内容。`}
        keywords="国产在线观看,关于我们,品牌介绍,联系方式"
        canonicalUrl={`${siteInfo.url}/about`}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
            { '@type': 'ListItem', position: 2, name: '关于我们' },
          ],
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 国产在线观看',
          description: `${siteInfo.name}品牌介绍与联系方式`,
          url: `${siteInfo.url}/about`,
        }}
      />

      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            <section className="py-10 md:py-14">
              <div className="text-center mb-12">
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                  关于我们
                </h1>
                <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                  {siteInfo.tagline}，{siteInfo.description.slice(0, 100)}...
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 md:gap-8 mb-14">
                {[
                  { label: '视频总量', value: siteInfo.stats.totalVideos, icon: 'ri-video-line' },
                  { label: '日活用户', value: siteInfo.stats.dailyUsers, icon: 'ri-user-line' },
                  { label: '月活用户', value: siteInfo.stats.monthlyActive, icon: 'ri-group-line' },
                  { label: '累计播放', value: siteInfo.stats.totalViews, icon: 'ri-eye-line' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center p-5 rounded-xl bg-background-100">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-3">
                      <i className={`${stat.icon} text-lg`}></i>
                    </span>
                    <div className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-xs text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="mb-14">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
                  品牌理念
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                  {brandValues.map((value) => (
                    <div key={value.title} className="p-6 rounded-xl bg-background-100 text-center">
                      <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-4">
                        <i className={`${value.icon} text-xl`}></i>
                      </span>
                      <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-2">
                        {value.title}
                      </h3>
                      <p className="text-xs text-foreground-600 leading-relaxed">{value.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-14">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
                  用户口碑
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {[
                    { name: '张先生', text: '找国产剧资源非常方便，画质清晰播放流畅，已经推荐给身边的朋友了！', rating: 5 },
                    { name: '李女士', text: '分类很全，电视剧、电影、综艺都有，每天都会上来看看有什么新内容更新。', rating: 5 },
                    { name: '王同学', text: '免费的国产视频平台能做得这么好真的难得，界面简洁，体验很棒！', rating: 4 },
                    { name: '赵先生', text: '高清画质真的赞，在家用大屏观看体验感拉满，国产剧爱好者的福音。', rating: 5 },
                    { name: '刘女士', text: '每天更新速度快，热门新剧基本上都能很快看到，太方便了。', rating: 5 },
                    { name: '陈先生', text: '不用注册就能看，简单直接，这才是真正为用户着想的平台。', rating: 4 },
                  ].map((review, i) => (
                    <div key={i} className="p-5 rounded-xl bg-background-100">
                      <div className="flex items-center gap-1 mb-3">
                        {Array.from({ length: 5 }).map((_, j) => (
                          <i
                            key={j}
                            className={`text-xs ${j < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                          ></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed mb-3">{review.text}</p>
                      <p className="text-xs text-foreground-500 font-medium">{review.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="py-10 md:py-14 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                  <div>
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                      联系我们
                    </h2>
                    <p className="text-sm text-foreground-600 mb-6 leading-relaxed">
                      如果您有任何问题或建议，欢迎通过以下方式联系我们，我们将尽快回复。
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                          <i className="ri-mail-line"></i>
                        </span>
                        <div>
                          <p className="text-xs text-foreground-500">电子邮箱</p>
                          <p className="text-sm font-medium text-foreground-800">{siteInfo.email}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                          <i className="ri-phone-line"></i>
                        </span>
                        <div>
                          <p className="text-xs text-foreground-500">客服电话</p>
                          <p className="text-sm font-medium text-foreground-800">{siteInfo.phone}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                          <i className="ri-map-pin-line"></i>
                        </span>
                        <div>
                          <p className="text-xs text-foreground-500">公司地址</p>
                          <p className="text-sm font-medium text-foreground-800">{siteInfo.address}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    {formUrl ? (
                      <form
                        id="contact-form"
                        data-readdy-form=""
                        onSubmit={handleSubmit}
                        className="bg-background-50 rounded-xl p-6 space-y-4"
                      >
                        <h3 className="font-heading font-semibold text-base text-foreground-950 mb-1">
                          发送消息
                        </h3>
                        <div>
                          <input
                            type="text"
                            name="name"
                            placeholder="您的姓名"
                            required
                            className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-white text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <input
                            type="email"
                            name="email"
                            placeholder="您的邮箱"
                            required
                            className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-white text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <textarea
                            name="message"
                            placeholder="请输入您的留言（500字以内）"
                            required
                            maxLength={500}
                            rows={4}
                            className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-white text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent resize-none"
                          ></textarea>
                        </div>
                        <input
                          type="text"
                          name="phone_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                          className="honeypot-field"
                        />
                        <button
                          type="submit"
                          className="w-full py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          发送消息
                        </button>
                        {formStatus === 'success' && (
                          <p className="text-sm text-green-600 text-center">消息发送成功，我们会尽快回复！</p>
                        )}
                        {formStatus === 'error' && (
                          <p className="text-sm text-red-500 text-center">{formError}</p>
                        )}
                      </form>
                    ) : (
                      <div className="bg-background-50 rounded-xl p-6 flex items-center justify-center h-full min-h-[200px]">
                        <p className="text-sm text-foreground-500">正在加载表单...</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            <section className="py-10 md:py-14">
              <div className="max-w-3xl mx-auto">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
                  常见问题
                </h2>
                <div className="space-y-3">
                  {faqs.slice(0, 4).map((faq, index) => (
                    <div
                      key={index}
                      className="rounded-lg border border-background-200/70 bg-background-50 overflow-hidden"
                    >
                      <button
                        className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      >
                        <h4 className="font-heading font-medium text-sm text-foreground-900 pr-4">
                          {faq.question}
                        </h4>
                        <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${openFaq === index ? 'rotate-180' : ''}`}>
                          <i className="ri-arrow-down-s-line text-foreground-500"></i>
                        </span>
                      </button>
                      {openFaq === index && (
                        <div className="px-5 pb-4">
                          <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}